import { MailOutlined } from '@ant-design/icons';
import { Modal, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import { formatDateString } from '../../Helpers/DateHelpers';
import { WebApiMemberDtoMemberCommunicationHistory } from '../../Repository/eventbookyapi';
import { TextService } from '../../Services/TextService';

interface CommunicationHistoryTableProps {
    history: WebApiMemberDtoMemberCommunicationHistory[];
    isLoading: boolean;
}

const CommunicationHistoryTable = ({ history, isLoading }: CommunicationHistoryTableProps) => {
    const GetText = TextService.GetText;
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewRenderKey, setPreviewRenderKey] = useState(0);
    const [previewHtml, setPreviewHtml] = useState('');
    const [selectedHistory, setSelectedHistory] = useState<WebApiMemberDtoMemberCommunicationHistory | null>(null);

    const isLikelyHtml = (content: string) => {
        return /<\/?[a-z][\s\S]*>/i.test(content);
    };

    const escapeHtml = (content: string) => {
        return content
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    const normalizePreviewHtml = (content: string | null | undefined) => {
        const trimmedContent = content?.trim();
        if (!trimmedContent) {
            return '';
        }

        let normalizedContent = trimmedContent;

        // Some environments return HTML as a JSON-encoded string: "<html>..."
        if (trimmedContent.startsWith('"') && trimmedContent.endsWith('"')) {
            try {
                const parsedContent = JSON.parse(trimmedContent);
                if (typeof parsedContent === 'string') {
                    normalizedContent = parsedContent;
                }
            } catch {
                normalizedContent = trimmedContent;
            }
        }

        if (isLikelyHtml(normalizedContent)) {
            return normalizedContent;
        }

        return `<html><body style="margin:16px;font-family:Arial,sans-serif;white-space:pre-wrap;line-height:1.5;">${escapeHtml(normalizedContent)}</body></html>`;
    };

    const isEmailCommunication = (communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => {
        if (communicationHistory.subject && communicationHistory.subject.trim().length > 0) {
            return true;
        }

        const content = communicationHistory.sentContent?.trim();
        return content ? isLikelyHtml(content) : false;
    };

    const mapCommunicationType = (communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => {
        return isEmailCommunication(communicationHistory)
            ? GetText('CommunicationHistory_Email')
            : GetText('CommunicationHistory_Sms');
    };

    const canPreview = (communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => {
        return isEmailCommunication(communicationHistory) && !!communicationHistory.sentContent?.trim();
    };

    const openPreviewModal = (communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => {
        setSelectedHistory(communicationHistory);
        setPreviewHtml(normalizePreviewHtml(communicationHistory.sentContent));
        setPreviewRenderKey((previous) => previous + 1);
        setIsPreviewModalOpen(true);
    };

    const closePreviewModal = () => {
        setIsPreviewModalOpen(false);
        setSelectedHistory(null);
    };

    const columns: ColumnsType<WebApiMemberDtoMemberCommunicationHistory> = [
        {
            title: '',
            key: 'preview',
            width: 50,
            render: (_, communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => (
                canPreview(communicationHistory)
                    ? <MailOutlined style={{ cursor: 'pointer', fontSize: '16px' }} onClick={() => openPreviewModal(communicationHistory)} />
                    : null
            ),
        },
        {
            title: GetText('EmailLogs_SentDate'),
            key: 'sent',
            render: (_, communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => (
                <span>{formatDateString(communicationHistory.sent)}</span>
            ),
        },
        {
            title: GetText('CommunicationHistory_Type'),
            key: 'type',
            render: (_, communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => (
                <span>{mapCommunicationType(communicationHistory)}</span>
            ),
        },
        {
            title: GetText('EmailLogs_Subject'),
            key: 'subject',
            render: (_, communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => (
                <span>{communicationHistory.subject || '-'}</span>
            ),
        },
        {
            title: GetText('EmailLogs_From'),
            key: 'from',
            render: (_, communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => (
                <span>{communicationHistory.sentFrom || '-'}</span>
            ),
        },
        {
            title: GetText('CommunicationHistory_SentBy'),
            key: 'createdByName',
            render: (_, communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => (
                <span>{communicationHistory.createdByName || '-'}</span>
            ),
        },
        {
            title: GetText('CommunicationHistory_SentToAmount'),
            key: 'sentToAmount',
            render: (_, communicationHistory: WebApiMemberDtoMemberCommunicationHistory) => (
                <span>{communicationHistory.sentToAmount ?? '-'}</span>
            ),
        },
    ];

    return <>
        <Table
            loading={isLoading}
            columns={columns}
            locale={{ emptyText: GetText('CommunicationHistory_EmptyMessage') }}
            rowKey={m => `${m.id ?? 0}-${m.sent?.toString() ?? ''}-${m.sentFrom ?? ''}`}
            pagination={{ position: ['topRight'] }}
            key='communicationHistoryId'
            dataSource={history}
        />

        <Modal
            title={GetText('CommunicationHistory_Details')}
            open={isPreviewModalOpen}
            footer={null}
            onCancel={closePreviewModal}
            width={900}
        >
            {selectedHistory ? (
                <div>
                    <p>
                        <b>{GetText('CommunicationHistory_Type')}:</b> {mapCommunicationType(selectedHistory)}
                    </p>
                    <p>
                        <b>{GetText('EmailLogs_SentDate')}:</b> {formatDateString(selectedHistory.sent)}
                    </p>
                    <p>
                        <b>{GetText('EmailLogs_Subject')}:</b> {selectedHistory.subject || '-'}
                    </p>
                    <p>
                        <b>{GetText('EmailLogs_From')}:</b> {selectedHistory.sentFrom || '-'}
                    </p>
                    <p>
                        <b>{GetText('CommunicationHistory_SentBy')}:</b> {selectedHistory.createdByName || '-'}
                    </p>
                    <p>
                        <b>{GetText('CommunicationHistory_SentToAmount')}:</b> {selectedHistory.sentToAmount ?? '-'}
                    </p>

                    {previewHtml ? (
                        <iframe
                            key={previewRenderKey}
                            title={GetText('CommunicationHistory_Details')}
                            srcDoc={previewHtml}
                            sandbox=""
                            style={{
                                width: '100%',
                                height: '620px',
                                border: '1px solid #f0f0f0',
                                borderRadius: '6px',
                                backgroundColor: '#ffffff'
                            }}
                        />
                    ) : (
                        <p>{GetText('CommunicationHistory_NoPreview')}</p>
                    )}
                </div>
            ) : null}
        </Modal>
    </>;
};

export default CommunicationHistoryTable;